'use client';

import * as Select from '@radix-ui/react-select';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useMemo, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { FormField, FormItem, FormMessage } from '@/components/atoms/form';
import Image from 'next/image';

type Option = {
  label: string;
  value: string;
};

interface FormattedCustomSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  options: Option[];
  placeholder?: string;
  className?: string;
}

export function FormattedOptimizedSelect<TFieldValues extends FieldValues>({
  control,
  name,
  options,
  placeholder = 'Selecione...',
  className,
}: FormattedCustomSelectProps<TFieldValues>) {
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, options]);

  const [parentRef, setParentRef] = useState<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef,
    estimateSize: () => 40,
    overscan: 10,
  });

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex-1">
          <Select.Root
            key={field.value ?? 'empty'}
            value={field.value}
            onValueChange={field.onChange}
          >
            <Select.Trigger
              aria-label={placeholder}
              className={clsx(
                'w-full inline-flex items-center data-[placeholder]:text-gray-600 justify-between rounded-md px-4 py-2 border border-gray-300 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-400',
                `${field.value ? 'bg-[#E8F0FE] text-black' : ''}`,
                className
              )}
            >
              <Select.Value
                placeholder={placeholder}
                className="text-left truncate"
              >
                {options.find((opt) => opt.value === field.value)?.label}
              </Select.Value>

              <Select.Icon>
                <Image
                  src="/icons/arrow-down-circle.png"
                  alt="Seta para baixo"
                  width={24}
                  height={24}
                />
              </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content
                className="bg-white border border-gray-300 rounded-md shadow-md z-50 w-[var(--radix-select-trigger-width)]"
                position="popper"
                sideOffset={4}
              >
                {/* Campo de busca */}
                <div className="p-2 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>

                {/* Lista virtualizada */}
                <div ref={setParentRef} className="h-52 overflow-auto relative">
                  <div
                    className="relative w-full"
                    style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const option = filteredOptions[virtualRow.index];
                      return (
                        <Select.Item
                          key={option.value}
                          value={option.value}
                          className="absolute top-0 left-0 w-full px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none cursor-pointer"
                          style={{
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          <Select.ItemText>{option.label}</Select.ItemText>
                        </Select.Item>
                      );
                    })}
                  </div>
                </div>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
